import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, Car, FileText, TrendingUp, CheckCircle2, ArrowRight, DollarSign,
  Scale, Send, Sparkles, AlertCircle,
} from 'lucide-react';
import './InsuranceRecoveryPage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHECKOUT_URL = 'https://www.commas.com/checkout/njr8PsGtWDnOqW0';

const HOW_IT_WORKS = [
  {
    icon: Send,
    title: 'Submit Your Case',
    desc: 'Tell us about the accident, the insurance company, and how they underpaid you. Two minutes.',
  },
  {
    icon: FileText,
    title: 'We Build The Demand',
    desc: 'Our team assembles a professional appraisal, evidence packet, and formal demand letter tuned to your state.',
  },
  {
    icon: Scale,
    title: 'We Negotiate',
    desc: 'We deliver the demand directly to the carrier and negotiate on your behalf until they pay what they owe.',
  },
  {
    icon: DollarSign,
    title: 'You Collect',
    desc: 'The insurance company cuts you a supplemental check. We take our success fee only from what we recover.',
  },
];

const WHAT_WE_RECOVER = [
  'Diminished Value — the resale hit your car takes after a repaired accident, even when fixed perfectly',
  'Underpaid Total-Loss Settlements — carriers routinely lowball ACV by $2K-$8K',
  'Rental Car & Loss-of-Use Reimbursement carriers "forgot" to pay',
  'Storage & Tow Fees the carrier tried to shift back to you',
  'Aftermarket & Custom-Part Value the carrier depreciated to zero',
  'Betterment / Depreciation deductions applied illegally in your state',
];

const FAQS = [
  {
    q: 'Are you attorneys?',
    a: 'No — we are professional claim recovery specialists. We prepare demand packages, assemble evidence, and negotiate directly with insurance carriers. We do not provide legal advice, and we do not represent you in court. For litigation-track cases, we can connect you with a partner attorney.',
  },
  {
    q: 'What does it cost?',
    a: 'A flat $599 Case Prep Fee covers your independent appraisal, evidence packet, and formal demand letter. If we recover money on your behalf, we take a 10% success fee from the recovery — nothing more. If we do not recover anything, you owe nothing beyond the prep fee.',
  },
  {
    q: 'How much can I recover?',
    a: 'Diminished value claims commonly recover $2,000 – $8,000 for a repaired mid-tier vehicle, and $5,000 – $25,000+ for luxury or newer vehicles. Underpaid total-loss cases often add $1,500 – $6,000. Every case is different.',
  },
  {
    q: 'How long does this take?',
    a: 'Most demand packages are prepared within 5-7 business days. Carrier response typically arrives in 15-45 days. Complex cases can run longer, especially if we escalate through the state Department of Insurance.',
  },
  {
    q: 'What if the accident was partly my fault?',
    a: 'Some diminished-value programs still pay out under comparative negligence rules. Tell us the details in the intake form — we will let you know if your case qualifies before charging the prep fee.',
  },
  {
    q: 'What states do you serve?',
    a: 'All 50 states. Diminished-value law varies by state — we tailor each demand to your state\'s specific statutes and case law before it is sent.',
  },
];

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  state: '',
  caseType: 'diminished-value',
  accidentDate: '',
  atFault: 'no',
  vehicleYear: '',
  vehicleMake: '',
  vehicleModel: '',
  insuranceCompany: '',
  claimAmount: '',
  description: '',
};

const InsuranceRecoveryPage = () => {
  const [searchParams] = useSearchParams();
  const isPaid = searchParams.get('paid') === '1' || searchParams.get('checkout') === 'complete';
  const intakeRef = useRef(null);

  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  // Auto-scroll to intake form when arriving from Commas post-checkout redirect
  useEffect(() => {
    if (isPaid && intakeRef.current) {
      const t = setTimeout(() => {
        intakeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [isPaid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.phone) {
      setStatus('error');
      setErrorMsg('Please fill in your name, email, and phone.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      await axios.post(`${API}/insurance-recovery/case`, {
        ...form,
        paid: isPaid,
        source: isPaid ? 'insurance-recovery-page-paid' : 'insurance-recovery-page',
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || err.message || 'Something went wrong.');
    }
  };

  return (
    <div className="dark-page ins-recovery-page">
      {/* Hero */}
      <section className="ins-hero">
        <div className="ins-hero-inner">
          <div className="ins-badge" data-testid="ins-badge">
            <ShieldCheck size={14} /> INSURANCE RECOVERY · DIMINISHED VALUE
          </div>
          <h1 className="display-huge">
            You Crashed.<br />
            They <span className="ins-hero-accent">Lowballed.</span><br />
            We Fight For The <span className="ins-hero-accent">Missing Money.</span>
          </h1>
          <p className="body-large ins-subtitle">
            Even a perfectly repaired car loses <strong>thousands in resale value</strong> the moment it hits a Carfax.
            Insurance carriers know this — they just hope you won't ask. We build the demand,
            negotiate the payout, and get you the check you're actually owed.
          </p>

          <div className="ins-hero-cta">
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary ins-cta-btn"
              data-testid="ins-cta-top"
            >
              Buy Case Prep — $599 <ArrowRight size={20} />
            </a>
            <a
              href="#intake"
              className="ins-cta-secondary"
              onClick={(e) => { e.preventDefault(); document.getElementById('intake')?.scrollIntoView({ behavior: 'smooth' }); }}
              data-testid="ins-cta-review"
            >
              Or request a free case review below
            </a>
            <span className="ins-hero-note">
              $599 case prep + 10% of recovery · No recovery, no success fee
            </span>
          </div>

          <div className="ins-hero-stats">
            <div className="ins-hero-stat">
              <div className="ins-hero-stat-num">$2K–$25K+</div>
              <div className="ins-hero-stat-label">Typical Recovery</div>
            </div>
            <div className="ins-hero-stat">
              <div className="ins-hero-stat-num">15-45 days</div>
              <div className="ins-hero-stat-label">Carrier Response</div>
            </div>
            <div className="ins-hero-stat">
              <div className="ins-hero-stat-num">50</div>
              <div className="ins-hero-stat-label">States Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="ins-steps">
        <h2 className="display-medium ins-section-title">How It Works</h2>
        <p className="ins-section-sub">
          Four steps between you and a check the carrier didn't want to cut.
        </p>
        <div className="ins-steps-grid">
          {HOW_IT_WORKS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="ins-step-card" data-testid={`ins-step-${i}`}>
                <div className="ins-step-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="ins-step-icon"><Icon size={22} /></div>
                <h3 className="ins-step-title">{s.title}</h3>
                <p className="ins-step-desc">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section className="ins-pricing">
        <div className="ins-pricing-card">
          <div className="ins-pricing-head">
            <Sparkles size={18} />
            <span>Transparent Pricing</span>
          </div>
          <div className="ins-pricing-rows">
            <div className="ins-pricing-row">
              <div className="ins-pricing-label">
                <strong>Case Prep Fee</strong>
                <span>Independent appraisal · evidence packet · formal demand letter · state-tuned strategy</span>
              </div>
              <div className="ins-pricing-value">$599</div>
            </div>
            <div className="ins-pricing-row">
              <div className="ins-pricing-label">
                <strong>Success Fee</strong>
                <span>Only if we recover money for you. Deducted from the payout.</span>
              </div>
              <div className="ins-pricing-value">10%</div>
            </div>
            <div className="ins-pricing-row ins-pricing-row-note">
              <div className="ins-pricing-label">
                <strong>No Recovery = No Success Fee</strong>
                <span>If the carrier refuses and we can't move them, the case prep fee is all you pay. Ever.</span>
              </div>
              <div className="ins-pricing-value ins-pricing-value-accent">$0</div>
            </div>
          </div>
          <div className="ins-pricing-foot">
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary ins-cta-btn"
              data-testid="ins-cta-pricing"
            >
              Buy Case Prep Now <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* What we recover */}
      <section className="ins-recover">
        <div className="ins-recover-inner">
          <h2 className="display-medium ins-section-title">What We Recover</h2>
          <p className="ins-section-sub">
            The line-items carriers systematically underpay or hope you won't notice.
          </p>
          <ul className="ins-recover-list">
            {WHAT_WE_RECOVER.map((line, i) => (
              <li key={i} data-testid={`ins-recover-${i}`}>
                <CheckCircle2 size={16} /> {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Intake form */}
      <section id="intake" ref={intakeRef} className="ins-intake">
        <div className="ins-intake-inner">
          {isPaid && status !== 'success' && (
            <div className="ins-paid-banner" data-testid="ins-paid-banner">
              <CheckCircle2 size={22} />
              <div>
                <strong>Payment received. Case slot reserved.</strong>
                <span>Fill in your case details below so our team can build your demand packet. This form takes about 2 minutes.</span>
              </div>
            </div>
          )}

          <h2 className="display-medium ins-section-title">
            {isPaid ? 'Send Us Your Case Details' : 'Not Ready to Buy? Get a Free Case Review'}
          </h2>
          <p className="ins-section-sub">
            {isPaid
              ? "You've paid the case prep fee — now we need the facts so we can build your demand. Everything below flows straight to our recovery team."
              : "Two minutes. Tell us about your case and we'll review it, then reach out within one business day to confirm it's viable before you commit to the $599 case prep."
            }
          </p>

          {status === 'success' ? (
            <div className="ins-form-success" data-testid="ins-form-success">
              <CheckCircle2 size={48} />
              <h3>{isPaid ? 'Case Details Received' : 'Case Submitted'}</h3>
              <p>
                {isPaid
                  ? 'Thanks — your case is officially in the queue. Our recovery team will build your demand packet within 5-7 business days and email you a status update.'
                  : "Thanks — we've received your case details and will be in touch within one business day. Check your email and phone."
                }
              </p>
              <Link to="/" className="btn-secondary ins-cta-btn">Back to Home</Link>
            </div>
          ) : (
            <form className="ins-form" onSubmit={handleSubmit} data-testid="ins-form">
              <div className="ins-form-grid">
                <label className="ins-field">
                  <span>First Name *</span>
                  <input name="firstName" value={form.firstName} onChange={handleChange} required data-testid="ins-firstName" />
                </label>
                <label className="ins-field">
                  <span>Last Name *</span>
                  <input name="lastName" value={form.lastName} onChange={handleChange} required data-testid="ins-lastName" />
                </label>
                <label className="ins-field">
                  <span>Email *</span>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required data-testid="ins-email" />
                </label>
                <label className="ins-field">
                  <span>Phone *</span>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} required data-testid="ins-phone" />
                </label>
                <label className="ins-field">
                  <span>State</span>
                  <input name="state" value={form.state} onChange={handleChange} placeholder="e.g. NV" data-testid="ins-state" />
                </label>
                <label className="ins-field">
                  <span>Case Type *</span>
                  <select name="caseType" value={form.caseType} onChange={handleChange} data-testid="ins-caseType">
                    <option value="diminished-value">Diminished Value (repaired car)</option>
                    <option value="insurance-recovery">Underpaid / Denied Claim</option>
                    <option value="total-loss">Underpaid Total-Loss Settlement</option>
                    <option value="both">Both / Not Sure</option>
                  </select>
                </label>
                <label className="ins-field">
                  <span>Accident Date</span>
                  <input type="date" name="accidentDate" value={form.accidentDate} onChange={handleChange} data-testid="ins-accidentDate" />
                </label>
                <label className="ins-field">
                  <span>Were You At Fault?</span>
                  <select name="atFault" value={form.atFault} onChange={handleChange} data-testid="ins-atFault">
                    <option value="no">No — other driver was at fault</option>
                    <option value="partial">Partially</option>
                    <option value="unsure">Unsure</option>
                    <option value="yes">Yes — I was at fault</option>
                  </select>
                </label>
                <label className="ins-field">
                  <span>Vehicle Year</span>
                  <input name="vehicleYear" value={form.vehicleYear} onChange={handleChange} placeholder="2022" maxLength="4" data-testid="ins-vehicleYear" />
                </label>
                <label className="ins-field">
                  <span>Vehicle Make</span>
                  <input name="vehicleMake" value={form.vehicleMake} onChange={handleChange} placeholder="Tesla" data-testid="ins-vehicleMake" />
                </label>
                <label className="ins-field">
                  <span>Vehicle Model</span>
                  <input name="vehicleModel" value={form.vehicleModel} onChange={handleChange} placeholder="Model Y" data-testid="ins-vehicleModel" />
                </label>
                <label className="ins-field">
                  <span>Insurance Company</span>
                  <input name="insuranceCompany" value={form.insuranceCompany} onChange={handleChange} placeholder="State Farm, Geico, etc." data-testid="ins-insuranceCompany" />
                </label>
                <label className="ins-field">
                  <span>Amount They Paid / Offered</span>
                  <input name="claimAmount" value={form.claimAmount} onChange={handleChange} placeholder="$0 or $8,500" data-testid="ins-claimAmount" />
                </label>
              </div>
              <label className="ins-field ins-field-full">
                <span>Tell Us What Happened</span>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Describe the accident, the repair, what the carrier said, and what you feel they're underpaying."
                  data-testid="ins-description"
                />
              </label>

              {status === 'error' && errorMsg && (
                <div className="ins-form-error" data-testid="ins-form-error">
                  <AlertCircle size={16} /> {errorMsg}
                </div>
              )}

              <div className="ins-form-submit">
                <button
                  type="submit"
                  className="btn-primary ins-cta-btn ins-cta-btn-lg"
                  disabled={status === 'submitting'}
                  data-testid="ins-submit"
                >
                  {status === 'submitting' ? 'Submitting…' : (isPaid ? 'Send Case Details' : 'Request Free Case Review')} <ArrowRight size={18} />
                </button>
                <p className="ins-form-note">
                  {isPaid
                    ? 'This goes straight to our recovery team. You will receive an email confirmation within a few minutes.'
                    : 'No credit card required. If we confirm your case is viable, next step is the $599 case prep — you can pay via the Buy button up top or the invoice we send you.'
                  }
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="ins-faq">
        <h2 className="display-medium ins-section-title">Common Questions</h2>
        <div className="ins-faq-list">
          {FAQS.map((f, i) => (
            <details className="ins-faq-item" key={i} data-testid={`ins-faq-${i}`}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="ins-disclaimer">
        <p>
          <strong>Not a law firm.</strong> Collins Academy Insurance Recovery is a claim
          preparation and negotiation service. We are not attorneys and do not provide legal advice.
          For claims requiring litigation, we can connect you with a partner attorney at your
          request. Results vary by state, carrier, vehicle, and case facts.
        </p>
      </section>
    </div>
  );
};

export default InsuranceRecoveryPage;
